import _ from 'lodash'
import { useEffect, useMemo } from 'react'
import { DeepPartial, useFormContext, useFormState, useWatch } from 'react-hook-form'

import {
  Schema,
  SchemaAdUnitBidderParams,
  SchemaAdUnitBidderProps,
} from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/form/schema'
import { UseErrorsProps } from './types'
import { getDirtyValues } from './utils/getDirtyValues'
import { setFormErrorsFor } from './utils/setFormErrorsFor'

type SchemaSlice = Pick<Schema, 'adUnitBidders'>

export const useErrors = ({ adUnitBidders }: UseErrorsProps) => {
  const { setError } = useFormContext<SchemaSlice>()

  const schema: DeepPartial<SchemaSlice> = useWatch<SchemaSlice>()
  const formState = useFormState<SchemaSlice>()

  const dirtyAdUnitBidders = useMemo(() => {
    if (_.isEmpty(schema.adUnitBidders)) {
      return {}
    } else {
      return getDirtyValues<SchemaSlice>(formState.dirtyFields, schema).adUnitBidders
    }
  }, [schema, formState])

  useEffect(() => {
    if (adUnitBidders.length === 0 || _.isEmpty(dirtyAdUnitBidders)) return

    Object.entries(dirtyAdUnitBidders).forEach(
      ([adUnitBidderId, adUnitBidderProps]: [
        key: keyof SchemaAdUnitBidderParams,
        value: DeepPartial<SchemaAdUnitBidderProps> | undefined,
      ]): void => {
        if (adUnitBidderProps === undefined) return
        const { csParams, s2sParams } = adUnitBidderProps
        const { errors } = formState

        if (csParams) {
          setFormErrorsFor({
            adUnitBidderId,
            adUnitBidders,
            errors,
            kind: 'cs',
            params: csParams,
            setError,
          })
        }

        if (s2sParams) {
          setFormErrorsFor({
            adUnitBidderId,
            adUnitBidders,
            errors,
            kind: 's2s',
            params: s2sParams,
            setError,
          })
        }
      },
    )
  }, [adUnitBidders, formState, dirtyAdUnitBidders])
}
